import { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  getCurrentUser,
  clearAuth,
  isImpersonating,
  stopImpersonation,
  getAuthMode,
} from '../../lib/auth.ts';
import { api } from '../../lib/api.ts';
import { extractError } from '../../lib/types.ts';
import { cn } from './cn.ts';
import { MapLogo } from './MapLogo.tsx';
import {
  LayoutDashboard,
  Users,
  Filter,
  Workflow,
  Megaphone,
  Activity,
  FileText,
  Building2,
  ChevronLeft,
  LogOut,
  ChevronDown,
  ChevronRight,
  Award,
  Plug,
  LogIn,
  ShieldCheck,
  CheckCircle,
  Settings,
  ExternalLink,
  LayoutTemplate,
  Key,
  Images,
  Layers,
  Zap,
  DollarSign,
  Link2,
  BarChart3,
  CalendarDays,
  MapPin,
  UsersRound,
  ClipboardList,
} from 'lucide-react';
import { useToast } from './Toast.tsx';
import { IconRail, getActiveArea } from './IconRail.tsx';
import { SpaceSidebar } from '../sidebars/SpaceSidebar.tsx';
import { LoyaltySidebar } from '../sidebars/LoyaltySidebar.tsx';
import { TopbarTabs } from './TopbarTabs.tsx';

// ─── Nav definitions ──────────────────────────────────────────────────────────

interface NavItem {
  to: string;
  icon: typeof LayoutDashboard;
  label: string;
  exact?: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const TENANT_SECTIONS: NavSection[] = [
  {
    label: '',
    items: [{ to: '/', icon: LayoutDashboard, label: 'Dashboard', exact: true }],
  },
  {
    label: 'Audience',
    items: [
      { to: '/customers', icon: Users, label: 'Customers' },
      { to: '/segments', icon: Filter, label: 'Segments' },
    ],
  },
  {
    label: 'Marketing',
    items: [
      { to: '/automations', icon: Workflow, label: 'Automations' },
      { to: '/campaigns', icon: Megaphone, label: 'Campaigns' },
      { to: '/templates', icon: FileText, label: 'Templates' },
    ],
  },
  {
    label: 'Events',
    items: [
      { to: '/events', icon: CalendarDays, label: 'Events' },
      { to: '/events/tours', icon: MapPin, label: 'Tours' },
    ],
  },
  {
    label: 'Loyalty',
    items: [{ to: '/loyalty', icon: Award, label: 'Programs' }],
  },
  {
    label: 'Content',
    items: [
      { to: '/spaces', icon: Layers, label: 'Spaces' },
      { to: '/media', icon: Images, label: 'Media' },
      { to: '/shortlinks', icon: Link2, label: 'Shortlinks' },
      { to: '/questionnaires', icon: ClipboardList, label: 'Questionnaires' },
    ],
  },
  {
    label: 'Data',
    items: [
      { to: '/signals', icon: Activity, label: 'Signals' },
      { to: '/signal-types', icon: Zap, label: 'Signal Types' },
      { to: '/analytics', icon: BarChart3, label: 'Analytics' },
    ],
  },
];

const TENANT_UTILITY: NavItem[] = [
  { to: '/integrations', icon: Plug, label: 'Integrations' },
  { to: '/cms/tokens', icon: Key, label: 'API Tokens' },
  { to: '/team', icon: UsersRound, label: 'Team' },
  { to: '/settings/properties', icon: Settings, label: 'Settings' },
];

const ADMIN_SECTIONS: NavSection[] = [
  {
    label: '',
    items: [
      { to: '/admin', icon: LayoutDashboard, label: 'Overview', exact: true },
      { to: '/admin/tenants', icon: Building2, label: 'Tenants' },
    ],
  },
  {
    label: 'Platform',
    items: [
      { to: '/admin/costs', icon: DollarSign, label: 'Costs' },
      { to: '/admin/status', icon: Activity, label: 'Status' },
    ],
  },
];

const SIDEBAR_KEY = 'map_sidebar_collapsed';

// ─── Tenant type ──────────────────────────────────────────────────────────────

interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: string;
}

// ─── Third-level sidebar (tenant mode) ───────────────────────────────────────
// Only shown when inside a specific space or loyalty program.

function shouldShowSidebar(area: string, pathname: string): boolean {
  if (area === 'content' && /^\/spaces\/[^/]+/.test(pathname)) return true;
  if (area === 'loyalty' && /^\/loyalty\/[^/]+/.test(pathname)) return true;
  return false;
}

function ThirdLevelSidebar({ area }: { area: string }) {
  switch (area) {
    case 'content': return <SpaceSidebar />;
    case 'loyalty': return <LoyaltySidebar />;
    default:        return null;
  }
}

// ─── Layout ───────────────────────────────────────────────────────────────────

export function Layout() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_KEY) === 'true');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const user = getCurrentUser();
  const impersonating = isImpersonating();
  const isAdminMode = getAuthMode() === 'admin';
  const activeArea = getActiveArea(location.pathname);

  // ── Platform status badge (alarm count — admin mode only, 60s polling) ─────
  // Uses the same query key as PlatformStatusPage so React Query deduplicates
  // in-flight requests when both are active simultaneously.
  const { data: statusBadge } = useQuery({
    queryKey: ['platform-status'],
    queryFn: () => api.get('/sa/platform/status').then((r) => r.data as { alarms: { state: string }[] | null }),
    refetchInterval: 60_000,
    staleTime: 55_000,
    enabled: isAdminMode,
  });
  const alarmCount = statusBadge?.alarms?.filter((a) => a.state === 'ALARM').length ?? 0;

  // ── Tenant switcher query (lazy — only when menu is open and super_admin) ───
  const { data: tenantList = [] } = useQuery<Tenant[]>({
    queryKey: ['admin-tenants-switcher'],
    queryFn: () => api.get('/sa/tenants').then((r) => r.data),
    enabled: userMenuOpen && user?.role === 'super_admin',
    staleTime: 60_000,
  });

  // ── Impersonate mutation (used by switcher) ──────────────────────────────
  const switchMutation = useMutation({
    mutationFn: (tenantId: string) =>
      api
        .post<{
          token: string;
          tenantId: string;
          tenantName: string;
          tenantSlug: string;
        }>(`/sa/tenants/${tenantId}/impersonate`, {})
        .then((r) => r.data),
    onSuccess: (data) => {
      setUserMenuOpen(false);
      const base = (import.meta.env.VITE_TENANT_BASE_DOMAIN as string | undefined)?.trim() ?? '';
      const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
      if (base && !isLocal) {
        window.location.href = `https://${data.tenantSlug}.${base}?token=${encodeURIComponent(data.token)}`;
      } else {
        // Local dev: no subdomain routing, stay on same origin and bootstrap token
        window.location.href = `/?token=${encodeURIComponent(data.token)}`;
      }
    },
    onError: (err) => toast.error(extractError(err)),
  });

  function toggleSidebar() {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_KEY, String(next));
      return next;
    });
  }

  function handleSignOut() {
    clearAuth();
    navigate('/login', { replace: true });
  }

  function handleExitTenant() {
    stopImpersonation();
    setUserMenuOpen(false);
    navigate('/admin', { replace: true });
  }

  return (
    <div className="flex h-screen bg-zinc-50 overflow-hidden">
      {/* ── Admin mode sidebar ────────────────────────────────────────────── */}
      {isAdminMode && (
      <aside
        className={cn(
          'flex flex-col bg-white border-r border-zinc-200 transition-all duration-200 ease-in-out flex-shrink-0',
          collapsed ? 'w-[52px]' : 'w-52',
        )}
      >
        {/* Logo */}
        <div className="flex items-center h-12 border-b border-zinc-100 px-3 gap-2 flex-shrink-0">
          {collapsed ? (
            <MapLogo variant="mark" theme="dark" className="w-7 h-7" />
          ) : (
            <MapLogo variant="compact" theme="dark" className="h-6" />
          )}
        </div>

        {/* Mode badge */}
        {!collapsed && isAdminMode && (
          <div className="px-3 pt-3 pb-1">
            <div className="flex items-center gap-1.5 px-2 py-1 bg-violet-50 rounded-md">
              <ShieldCheck size={11} className="text-violet-500 flex-shrink-0" />
              <span className="text-[10px] font-semibold text-violet-600 uppercase tracking-widest">
                Admin
              </span>
            </div>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2 px-2">
          <>
              {ADMIN_SECTIONS.map((section) => (
                <div key={section.label || '_top'}>
                  {section.label ? (
                    collapsed ? (
                      <div className="border-t border-zinc-100 mx-1 my-1.5" />
                    ) : (
                      <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider px-2 mt-3.5 mb-1">
                        {section.label}
                      </p>
                    )
                  ) : null}
                  <div className="space-y-0.5">
                    {section.items.map(({ to, icon: Icon, label, exact }) => {
                      const isStatusItem = to === '/admin/status';
                      return (
                        <NavLink
                          key={to}
                          to={to}
                          end={exact}
                          title={collapsed ? label : undefined}
                          className={({ isActive }) =>
                            cn(
                              'flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm font-medium transition-colors',
                              collapsed ? 'justify-center' : '',
                              isActive
                                ? 'bg-zinc-100 text-zinc-900'
                                : 'text-zinc-500 hover:bg-zinc-50 hover:text-zinc-800',
                            )
                          }
                        >
                          <Icon size={15} className="flex-shrink-0" />
                          {!collapsed && <span className="truncate flex-1">{label}</span>}
                          {!collapsed && isStatusItem && alarmCount > 0 && (
                            <span className="ml-auto flex-shrink-0 flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold">
                              {alarmCount}
                            </span>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                </div>
              ))}

            </>
        </nav>

        {/* Impersonation banner */}
        {impersonating && (
          <div
            className={cn(
              'border-t border-amber-200 bg-amber-50 flex-shrink-0',
              collapsed ? 'p-2' : 'px-3 py-2.5',
            )}
          >
            {collapsed ? (
              <button
                onClick={handleExitTenant}
                title="Exit tenant"
                className="flex items-center justify-center w-full h-7 rounded-md text-amber-600 hover:text-amber-800 hover:bg-amber-100 transition-colors"
              >
                <LogIn size={14} className="rotate-180" />
              </button>
            ) : (
              <div className="flex items-center gap-2 min-w-0">
                <Building2 size={13} className="text-amber-500 flex-shrink-0" />
                <span className="text-xs font-medium text-amber-800 truncate flex-1 min-w-0">
                  {user?.tenantName ?? user?.tenantId ?? 'Tenant'}
                </span>
                <button
                  onClick={handleExitTenant}
                  title="Exit tenant"
                  className="flex-shrink-0 text-[10px] font-medium text-amber-600 hover:text-amber-900 hover:underline"
                >
                  Exit
                </button>
              </div>
            )}
          </div>
        )}

        {/* Collapse toggle */}
        <div className="border-t border-zinc-100 p-2 flex-shrink-0">
          <button
            onClick={toggleSidebar}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="flex items-center justify-center w-full h-7 rounded-md text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50 transition-colors"
          >
            <ChevronLeft
              size={15}
              className={cn('transition-transform duration-200', collapsed && 'rotate-180')}
            />
          </button>
        </div>
      </aside>
      )}

      {/* ── Tenant mode: icon rail ────────────────────────────────────────── */}
      {!isAdminMode && <IconRail />}

      {/* ── Main area (topbar + optional sidebar + content) ───────────────── */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Topbar */}
        <header className="flex-shrink-0 h-12 bg-white border-b border-zinc-200 flex items-center justify-between px-4 gap-4">
          {!isAdminMode ? <TopbarTabs area={activeArea} /> : <div className="flex-1 min-w-0" />}

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen((v) => !v)}
              className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-zinc-50 transition-colors text-sm text-zinc-700"
            >
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-zinc-200 text-zinc-700 text-xs font-medium">
                {(user?.name ?? user?.email ?? user?.sub ?? '?')[0]?.toUpperCase()}
              </span>
              <span className="hidden sm:block text-xs font-medium truncate max-w-[120px]">
                {user?.name ?? user?.email ?? 'User'}
              </span>
              {impersonating && (
                <span className="hidden sm:flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                  <Building2 size={9} />
                  {user?.tenantName ?? 'Tenant'}
                </span>
              )}
              <ChevronDown size={12} className="text-zinc-400" />
            </button>

            {userMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1.5 z-20 w-56 bg-white border border-zinc-200 rounded-xl shadow-lg overflow-hidden">
                  {/* User identity */}
                  {user && (
                    <div className="px-3 py-2.5 border-b border-zinc-100">
                      <p className="text-xs font-medium text-zinc-900 truncate">
                        {user.email ?? user.sub}
                      </p>
                      <p className="text-[10px] text-zinc-400 mt-0.5 flex items-center gap-1">
                        {isAdminMode ? (
                          <>
                            <ShieldCheck size={9} className="text-violet-500" />
                            <span className="text-violet-600 font-medium">super_admin</span>
                          </>
                        ) : impersonating ? (
                          <>
                            <Building2 size={9} className="text-amber-500" />
                            <span>super_admin · viewing {user.tenantName}</span>
                          </>
                        ) : (
                          <>
                            <span>{user.role}</span>
                            {user.tenantId && <span>· {user.tenantId.slice(0, 8)}…</span>}
                          </>
                        )}
                      </p>
                    </div>
                  )}

                  {/* Workspace switcher — visible to super_admin only */}
                  {user?.role === 'super_admin' && (
                    <div className="border-b border-zinc-100">
                      <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
                        Workspaces
                      </p>
                      <div className="pb-1 max-h-40 overflow-y-auto">
                        {tenantList.length === 0 ? (
                          <p className="px-3 py-2 text-xs text-zinc-400">No tenants yet</p>
                        ) : (
                          tenantList.map((t) => {
                            const isCurrent = impersonating && user?.tenantId === t.id;
                            const isLoading =
                              switchMutation.isPending && switchMutation.variables === t.id;
                            return (
                              <button
                                key={t.id}
                                disabled={isLoading || t.status !== 'active'}
                                onClick={() => !isCurrent && switchMutation.mutate(t.id)}
                                className={cn(
                                  'flex items-center gap-2.5 w-full px-3 py-2 text-sm transition-colors',
                                  isCurrent
                                    ? 'text-zinc-900 bg-zinc-50 cursor-default'
                                    : t.status === 'active'
                                      ? 'text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900'
                                      : 'text-zinc-300 cursor-not-allowed',
                                )}
                              >
                                <span className="flex items-center justify-center w-5 h-5 rounded bg-zinc-100 text-zinc-500 text-[10px] font-bold flex-shrink-0">
                                  {t.name[0]?.toUpperCase()}
                                </span>
                                <span className="flex-1 text-left truncate text-xs font-medium">
                                  {t.name}
                                </span>
                                {isCurrent && (
                                  <CheckCircle
                                    size={12}
                                    className="text-emerald-500 flex-shrink-0"
                                  />
                                )}
                                {!isCurrent && t.status === 'active' && !isLoading && (
                                  <ChevronRight size={11} className="text-zinc-300 flex-shrink-0" />
                                )}
                                {isLoading && (
                                  <span className="text-[10px] text-zinc-400 animate-pulse flex-shrink-0">
                                    …
                                  </span>
                                )}
                                {t.status !== 'active' && (
                                  <span className="text-[9px] text-zinc-300 flex-shrink-0">
                                    {t.status}
                                  </span>
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}

                  {/* Exit tenant — only when impersonating */}
                  {impersonating && (
                    <button
                      onClick={handleExitTenant}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-amber-600 hover:text-amber-900 hover:bg-amber-50 transition-colors border-b border-zinc-100"
                    >
                      <LogIn size={13} className="rotate-180" />
                      Exit to admin panel
                    </button>
                  )}

                  {/* Sign out */}
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50 transition-colors"
                  >
                    <LogOut size={13} />
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        {/* Below topbar: optional 3rd-level sidebar + page content */}
        <div className="flex flex-1 min-h-0">
          {!isAdminMode && shouldShowSidebar(activeArea, location.pathname) && (
            <aside className="flex flex-col bg-white border-r border-zinc-200 w-52 shrink-0 overflow-y-auto">
              <ThirdLevelSidebar area={activeArea} />
            </aside>
          )}
          <main className="flex-1 overflow-auto">
            <div className="p-6">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
